import { createRootRoute, createRoute, createRouter, Outlet, redirect } from "@tanstack/react-router";
import { lazy } from "react";
import { landingPath } from "./lib/landing";
import { NotFoundPage } from "./routes/not-found";

const rootRoute = createRootRoute({ component: () => <Outlet />, notFoundComponent: NotFoundPage });

// Candidate routes (owned by web/routes/candidate/*)
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: async () => {
    throw redirect({ to: await landingPath(), replace: true });
  },
});
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/i/$token",
  component: lazy(() => import("./routes/candidate/invite")),
});
const verifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify",
  component: lazy(() => import("./routes/candidate/verify")),
  validateSearch: (s: Record<string, unknown>) => ({
    error: typeof s.error === "string" ? s.error : undefined,
  }),
});
const consentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/consent",
  component: lazy(() => import("./routes/candidate/consent")),
});
const agreementRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agreement",
  component: lazy(() => import("./routes/candidate/agreement")),
});
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
  component: lazy(() => import("./routes/candidate/app")),
});
const chatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/chat",
  component: lazy(() => import("./routes/candidate/chat")),
});
const finishedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/finished",
  component: lazy(() => import("./routes/candidate/finished")),
});
const expiredRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/expired",
  component: lazy(() => import("./routes/candidate/expired")),
  validateSearch: (s: Record<string, unknown>) => ({
    reason: typeof s.reason === "string" ? s.reason : undefined,
  }),
});

// Admin routes (owned by web/routes/admin/*). Layout route handles Access/admin gate.
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  component: lazy(() => import("./routes/admin/layout")),
});
const adminIndex = createRoute({
  getParentRoute: () => adminRoute,
  path: "/",
  component: lazy(() => import("./routes/admin/dashboard")),
});
const adminNew = createRoute({
  getParentRoute: () => adminRoute,
  path: "/assignments/new",
  component: lazy(() => import("./routes/admin/new-assignment")),
});
const adminDetail = createRoute({
  getParentRoute: () => adminRoute,
  path: "/assignments/$id",
  component: lazy(() => import("./routes/admin/assignment-detail")),
});
const adminInstances = createRoute({
  getParentRoute: () => adminRoute,
  path: "/instances",
  component: lazy(() => import("./routes/admin/instances")),
});
const adminSettings = createRoute({
  getParentRoute: () => adminRoute,
  path: "/settings",
  component: lazy(() => import("./routes/admin/settings")),
});

// Dev SPA routes (non-production only; /api/dev/* returns 404 in production and the SPA hides links)
const devOverviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dev",
  validateSearch: (search: Record<string, unknown>): { workspace?: "written" } =>
    search.workspace === "written" ? { workspace: "written" } : {},
  component: lazy(() => import("./routes/dev/overview")),
});
const styleguideRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dev/styleguide",
  component: lazy(() => import("./routes/dev/styleguide")),
});
const devStatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dev/states",
  component: lazy(() => import("./routes/dev/states")),
});
const adminPreviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dev/admin-preview",
  component: lazy(() => import("./routes/dev/admin-preview")),
});
const devEmailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dev/emails",
  component: lazy(() => import("./routes/dev/emails")),
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  inviteRoute,
  verifyRoute,
  consentRoute,
  agreementRoute,
  appRoute,
  chatRoute,
  expiredRoute,
  finishedRoute,
  adminRoute.addChildren([adminIndex, adminNew, adminDetail, adminInstances, adminSettings]),
  devOverviewRoute,
  styleguideRoute,
  devStatesRoute,
  adminPreviewRoute,
  devEmailsRoute,
]);

export const router = createRouter({ routeTree, defaultPreload: "intent", scrollRestoration: true });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
